/**
 * ImageUploader — composant réutilisable de gestion de photos signalements (L13.8).
 *
 * Fonctionnalités :
 *   - Capture caméra mobile via <input capture="environment"> (déclenche
 *     l'app appareil photo iOS/Android, pas la galerie)
 *   - Sélection fichier alternative (icône galerie pour choisir dans les photos
 *     existantes du téléphone, utile aussi sur desktop)
 *   - Preview thumbnail avant envoi
 *   - Indicateur de progress pendant l'upload
 *   - Galerie des photos déjà uploadées (chargement lazy des signed URLs au clic
 *     "Voir" pour économiser les appels API si l'agent ne consulte pas)
 *   - Bouton supprimer par photo (avec confirmation Dialog R13)
 *
 * Le composant est agnostique : il appelle des callbacks fournis par le parent
 * (`onUpload`, `onDelete`) qui font les vrais appels API et reload la liste.
 *
 * Limites enforced côté client (re-vérifiées côté serveur) :
 *   - 10 MB max par photo
 *   - 6 photos max par signalement (toutes catégories confondues)
 *   - MIME accepté : image/jpeg, image/png, image/webp, image/heic, image/heif, image/avif
 */

import { useState, useRef, useEffect } from 'react';
import { Button, Alert } from '@/components/ui';
import { Camera, ImagePlus, Trash2, AlertCircle, Eye, Loader2, X } from 'lucide-react';
import { useConfirmDialog } from '@/lib/use-confirm-dialog'; // R13
import { api, ApiException } from '@/lib/api';
import type { SignalementPhoto, SignalementPhotoKind } from '@roronoa-collectivites/shared-types';

const ACCEPT_MIME = 'image/jpeg,image/png,image/webp,image/heic,image/heif,image/avif';
const MAX_SIZE_MB = 10;
const KIND_LABEL: Record<SignalementPhotoKind, string> = {
  admin_initial: 'Photo initiale',
  agent_avant: 'Avant intervention',
  agent_apres: 'Après intervention',
};
const KIND_ICON: Record<SignalementPhotoKind, typeof Camera> = {
  admin_initial: ImagePlus,
  agent_avant: Camera,
  agent_apres: Camera,
};

interface ImageUploaderProps {
  /** ID du signalement parent */
  signalementId: string;
  /** Catégorie cible des nouvelles photos uploadées par ce composant */
  kind: SignalementPhotoKind;
  /** Photos déjà associées au signalement (toutes catégories) — pour la galerie */
  existingPhotos: SignalementPhoto[];
  /** Callback après upload réussi (le parent re-fetch et passe les nouvelles photos) */
  onUploadSuccess?: (photo: SignalementPhoto) => void;
  /** Callback après suppression réussie */
  onDeleteSuccess?: (photoId: string) => void;
  /** Si false, n'affiche que la galerie (lecture seule) */
  canUpload?: boolean;
  /** Si false, masque les boutons de suppression */
  canDelete?: boolean;
  /** Nombre max de photos toutes catégories (default 6) */
  maxPhotos?: number;
  /** Légende affichée au-dessus du bouton upload */
  label?: string;
}

export function ImageUploader({
  signalementId,
  kind,
  existingPhotos,
  onUploadSuccess,
  onDeleteSuccess,
  canUpload = true,
  canDelete = true,
  maxPhotos = 6,
  label,
}: ImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { confirm, dialogElement } = useConfirmDialog();

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Mapping photoId → signed URL (chargé à la demande pour économiser les appels)
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [loadingUrl, setLoadingUrl] = useState<string | null>(null);

  // Cleanup le blob URL de preview à l'unmount
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ----- Filtrer photos par kind pour affichage groupé ------------------
  const photosThisKind = existingPhotos.filter((p) => p.kind === kind);
  const totalPhotos = existingPhotos.length;
  const canAddMore = canUpload && totalPhotos < maxPhotos && !uploading;

  // ----- Handlers upload ------------------------------------------------

  async function handleFile(file: File | null) {
    if (!file) return;
    setError(null);

    // Validation client
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Fichier trop volumineux (max ${MAX_SIZE_MB} MB). Réduis la qualité dans ton appareil photo ou utilise une app de compression.`);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Le fichier doit être une image.');
      return;
    }

    // Preview avant upload
    const blobUrl = URL.createObjectURL(file);
    setPreviewUrl(blobUrl);

    setUploading(true);
    setUploadProgress(10);

    try {
      // Note : fetch standard ne donne pas de progress upload natif.
      // On affiche un progress simulé "en cours" pour l'UX, l'utilisateur
      // n'attend que quelques secondes pour 10 MB max.
      const interval = window.setInterval(() => {
        setUploadProgress((p) => (p < 85 ? p + 5 : p));
      }, 200);

      const photo = await api.serviceTechnique.signalements.uploadPhoto(
        signalementId,
        kind,
        file
      );

      window.clearInterval(interval);
      setUploadProgress(100);

      // Le parent fait son re-fetch via onUploadSuccess
      onUploadSuccess?.(photo);

      // Reset après succès
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setPreviewUrl(null);
        URL.revokeObjectURL(blobUrl);
      }, 400);
    } catch (err) {
      setUploading(false);
      setUploadProgress(0);
      setPreviewUrl(null);
      URL.revokeObjectURL(blobUrl);
      if (err instanceof ApiException) {
        const code = err.payload.code;
        if (code === 'STORAGE_UNAVAILABLE') {
          setError('Le service de stockage des photos n\'est pas encore configuré. Contacte ton administrateur.');
        } else if (code === 'FILE_TOO_LARGE') {
          setError(`Fichier trop volumineux (max ${MAX_SIZE_MB} MB).`);
        } else if (code === 'PHOTO_LIMIT_REACHED') {
          setError(`Limite atteinte : ${maxPhotos} photos maximum par signalement.`);
        } else if (code === 'UNSUPPORTED_MEDIA_TYPE') {
          setError('Type de fichier non supporté. Utilise JPEG, PNG, WebP, HEIC ou AVIF.');
        } else {
          setError(err.payload.error);
        }
      } else {
        setError('Erreur lors de l\'upload. Réessaie.');
      }
    } finally {
      // Reset l'input pour pouvoir re-uploader le même fichier
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  }

  // ----- Handler signed URL on-demand ----------------------------------

  async function loadSignedUrl(photoId: string): Promise<string | null> {
    if (signedUrls[photoId]) return signedUrls[photoId];
    setLoadingUrl(photoId);
    try {
      const result = await api.serviceTechnique.signalements.getPhotoSignedUrl(
        signalementId,
        photoId
      );
      setSignedUrls((prev) => ({ ...prev, [photoId]: result.url }));
      return result.url;
    } catch (err) {
      if (err instanceof ApiException && err.payload.code !== 'STORAGE_UNAVAILABLE') {
        setError(err.payload.error);
      }
      return null;
    } finally {
      setLoadingUrl(null);
    }
  }

  async function handleViewPhoto(photo: SignalementPhoto) {
    const url = await loadSignedUrl(photo.id);
    if (url) {
      // Ouverture dans nouvel onglet (signed URL R2 valide 1h)
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  // ----- Handler delete ------------------------------------------------

  async function handleDelete(photo: SignalementPhoto) {
    const ok = await confirm({
      title: 'Supprimer cette photo ?',
      description: `Cette ${KIND_LABEL[photo.kind].toLowerCase()} sera placée dans la corbeille (rétention 30 jours).`,
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;

    try {
      await api.serviceTechnique.signalements.deletePhoto(signalementId, photo.id);
      onDeleteSuccess?.(photo.id);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error);
      } else {
        setError('Erreur lors de la suppression.');
      }
    }
  }

  // ----- Render ---------------------------------------------------------

  const KindIcon = KIND_ICON[kind];

  return (
    <div className="space-y-3">
      {dialogElement}

      {label && (
        <div className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
          {label}
        </div>
      )}

      {error && (
        <Alert variant="error" icon={AlertCircle}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">{error}</div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-ink-muted hover:text-ink"
              aria-label="Fermer l'erreur"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </Alert>
      )}

      {/* ===== Galerie des photos existantes de cette catégorie ===== */}
      {photosThisKind.length > 0 && (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" role="list">
          {photosThisKind.map((photo) => (
            <li
              key={photo.id}
              className="relative aspect-square overflow-hidden rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass"
            >
              {/* Thumbnail : on charge la signed URL au mount silencieusement */}
              <PhotoThumb
                photo={photo}
                signedUrl={signedUrls[photo.id] ?? null}
                onLoadUrl={() => loadSignedUrl(photo.id)}
                isLoading={loadingUrl === photo.id}
              />

              {/* Overlay actions */}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/60 to-transparent px-2 py-1.5">
                <button
                  type="button"
                  onClick={() => handleViewPhoto(photo)}
                  className="inline-flex items-center gap-1 rounded-r-pill bg-white/20 px-2 py-0.5 text-2xs text-white backdrop-blur-glass hover:bg-white/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  aria-label="Voir la photo en grand"
                >
                  <Eye className="h-3 w-3" aria-hidden="true" />
                  Voir
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(photo)}
                    className="inline-flex items-center gap-1 rounded-r-pill bg-urgent/80 px-2 py-0.5 text-2xs text-white backdrop-blur-glass hover:bg-urgent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label="Supprimer cette photo"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden="true" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ===== Preview du fichier en cours d'upload ===== */}
      {uploading && previewUrl && (
        <div className="relative aspect-video overflow-hidden rounded-r-lg border border-tech/30 bg-tech-tint/30">
          <img src={previewUrl} alt="Aperçu de la photo en cours d'envoi" className="h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-tech-fg">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
            <div className="text-2xs font-medium">Envoi en cours… {uploadProgress}%</div>
            <div className="h-1 w-32 overflow-hidden rounded-full bg-surface">
              <div
                className="h-full bg-tech transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ===== Boutons d'ajout (mobile = caméra + galerie, desktop = juste galerie) ===== */}
      {canAddMore && !uploading && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Caméra directe (iOS/Android) — capture="environment" force la caméra arrière */}
          <input
            ref={cameraInputRef}
            type="file"
            accept={ACCEPT_MIME}
            capture="environment"
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            aria-label="Prendre une photo avec l'appareil"
          />
          <Button
            type="button"
            variant="primary"
            onClick={() => cameraInputRef.current?.click()}
          >
            <KindIcon className="h-4 w-4" aria-hidden="true" />
            Prendre une photo
          </Button>

          {/* Galerie / fichier (utile sur desktop ou si l'agent a déjà la photo) */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_MIME}
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            aria-label="Choisir une photo existante"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImagePlus className="h-4 w-4" aria-hidden="true" />
            Choisir un fichier
          </Button>

          <span className="text-2xs text-ink-subtle">
            {totalPhotos}/{maxPhotos} photos · max {MAX_SIZE_MB} MB par image
          </span>
        </div>
      )}

      {/* ===== Message limite atteinte ===== */}
      {canUpload && !canAddMore && !uploading && totalPhotos >= maxPhotos && (
        <p className="text-2xs text-ink-subtle">
          Limite de {maxPhotos} photos atteinte. Supprime-en une pour en ajouter une nouvelle.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// PhotoThumb — affiche la miniature d'une photo (signed URL lazy)
// ============================================================================

function PhotoThumb({
  photo,
  signedUrl,
  onLoadUrl,
  isLoading,
}: {
  photo: SignalementPhoto;
  signedUrl: string | null;
  onLoadUrl: () => Promise<string | null>;
  isLoading: boolean;
}) {
  // Auto-load la signed URL au mount (pour afficher la miniature)
  useEffect(() => {
    if (!signedUrl && !isLoading) {
      void onLoadUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo.id]);

  if (isLoading || !signedUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center text-ink-subtle">
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt={KIND_LABEL[photo.kind]}
      className="h-full w-full object-cover"
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  );
}
